'use client';

import { Fragment, useContext, useEffect, useId, useRef, type ReactNode } from 'react';
import { ListIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { recordStat } from '@/shared/functions';
import { StatsContext } from '@/app/contexts';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import type { NavMenuKey, OverflowMenuItems } from './use-primary-nav-overflow';

/**
 * Portaled surfaces that belong to the open hamburger. CJIS uses Popover
 * (`.tp-popover`). The language list is a Menu, which portals as
 * `.tp-popover.tp-menu-popover`. `.tp-language-menu` is the in-place trigger
 * root, including the bar picker above 639px, so it must still dismiss.
 */
const MENU_OWNED_PORTAL = ['tp-popover', 'tp-menu-popover'] as const;

/** True when the event started inside the panel, its trigger, or a portaled child. */
function pointerDownBelongsToMenu(
  event: Pick<Event, 'composedPath'>,
  panel: HTMLElement | null,
  trigger: HTMLElement | null,
): boolean {
  for (const node of event.composedPath()) {
    if (!(node instanceof Element)) continue;
    if (node === panel || node === trigger) return true;
    if (MENU_OWNED_PORTAL.some((className) => node.classList.contains(className))) return true;
  }
  return false;
}

type PrimaryNavMenuProps = {
  visible: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  menuKeys: NavMenuKey[];
  menuItems: OverflowMenuItems;
  /** Rows after the overflow items and before the footer. Null entries are skipped. */
  utilityItems?: ReactNode[];
  footer?: ReactNode;
};

/**
 * Hamburger trigger plus the full-width drop-down panel — Serenity "Mobile
 * Navigation" (Figma 65:3913). The panel is absolutely positioned against the
 * fixed nav, so it renders here next to the trigger without affecting layout.
 */
export function PrimaryNavMenu({
  visible,
  isOpen,
  onOpenChange,
  menuKeys,
  menuItems,
  utilityItems,
  footer,
}: PrimaryNavMenuProps) {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  const panelId = `primary-nav-menu-${useId()}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const isPanelOpen = visible && isOpen;
  const utilities = (utilityItems ?? []).filter((item) => item != null && item !== false);

  // Dropping `Popover` means dismissal is ours to own: Escape, outside pointer,
  // and returning focus to the trigger.
  useEffect(() => {
    if (!isPanelOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onOpenChange(false);
      triggerRef.current?.focus();
    };

    const handlePointerDown = (event: PointerEvent) => {
      // Use the dispatch path, not closest() after the fact. A nested menu can
      // unmount during this gesture, and a text-node target is not an Element.
      if (pointerDownBelongsToMenu(event, panelRef.current, triggerRef.current)) return;
      onOpenChange(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isPanelOpen, onOpenChange]);

  // Widening past the last collapse level removes the trigger; don't strand the panel open.
  useEffect(() => {
    if (!visible && isOpen) {
      onOpenChange(false);
    }
  }, [visible, isOpen, onOpenChange]);

  if (!visible) {
    return null;
  }

  return (
    <>
      <Button
        ref={triggerRef}
        iconOnly
        size="lg"
        style="text"
        color="light"
        background="brand"
        aria-label={isPanelOpen ? t('nav.closeMenu') : t('nav.openMenu')}
        aria-expanded={isPanelOpen}
        aria-controls={panelId}
        className="tp-primary-navigation__menu-trigger shrink-0"
        id="OverflowMenuButton"
        onClick={(event) => {
          recordStat('OverflowMenuButtonClicked', event, statsContext);
          onOpenChange(!isPanelOpen);
        }}
      >
        {isPanelOpen ? <XIcon weight="bold" /> : <ListIcon weight="bold" />}
      </Button>

      {isPanelOpen && (
        <div
          ref={panelRef}
          id={panelId}
          role="menu"
          aria-label={t('nav.menu')}
          className="tp-primary-navigation__menu"
        >
          {menuKeys.map((key, index) => (
            <Fragment key={key}>
              {index > 0 && <hr className="tp-primary-navigation__menu-divider" />}
              <div
                id={`OverflowMenuButton-${key}`}
                role="none"
                onClick={(event) => {
                  recordStat(`OverflowMenuButton${key}Clicked`, event, statsContext);
                }}
              >
                {menuItems[key]}
              </div>
            </Fragment>
          ))}
          {utilities.map((item, index) => (
            <Fragment key={`utility-${index}`}>
              {(menuKeys.length > 0 || index > 0) && (
                <hr className="tp-primary-navigation__menu-divider" />
              )}
              <div role="none">{item}</div>
            </Fragment>
          ))}
          {footer != null && (
            <>
              {(menuKeys.length > 0 || utilities.length > 0) && (
                <hr className="tp-primary-navigation__menu-divider" />
              )}
              {footer}
            </>
          )}
        </div>
      )}
    </>
  );
}
